import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState, getState } from '../../store/models/app.state';
import { EventService } from '../event.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { addLoader, errorPopup, globalLoading, removeLoader, successPopup } from '../../store/actions/ui.actions';
import {
  checkCurrentEventForPendingChanges,
  createProjectEvent,
  deleteProjectEvent,
  dispatchedCreateUpdateEvent,
  loadProjectEventsCommentsState,
  loadEventNotificationsListByEventId,
  loadProjectEventListByProjectId,
  pendingCreateUpdateEvent,
  populateCurrentProjectEvent,
  populateCurrentProjectEventSnapshot,
  populateProjectEventsCommentsState,
  removeProjectEvent,
  successCreateUpdateEvent,
  updateEventField,
  updateEventNotificationsList,
  updateProjectEvent,
  updateProjectEventInList,
  updateProjectEventList,
  updateProjectEvents,
  updateProjectEventsInList,
  softDeleteProjectEvent,
  createEventGroup,
  updateEventGroup,
  deleteEventGroup,
  generateEventsInGroup,
  softDeleteEventGroup,
} from './event.actions';
import { isEqual } from 'lodash-es';
import { ProjectEventModel } from '../project-event.model';
import { EventNotificationsService, IEventNotificationModel } from '../event-notifications';
import { ICommentsState } from '../../shared/models/comments-state.interface';
import { selectListProjectEventsState } from './event.state';
import { IProjectEventsResponse } from '../project-events-response.interface';
import { IEventGroup } from '../event.model';
import { ElementTypeEnum } from '../enums/element-type.enum';
import { Statuses } from '../../shared/models/statuses.enum';
import { UtilsService } from '../../core/utils.service';

@Injectable()
export class EventEffects {
  store = inject(Store<AppState>);
  actions = inject(Actions);
  eventService = inject(EventService);
  eventNotificationsService = inject(EventNotificationsService);
  utilsService: UtilsService = inject(UtilsService);

  updateEventField = createEffect(() => {
    return this.actions.pipe(
      ofType(updateEventField, successCreateUpdateEvent),
      mergeMap(() => {
        return [
          checkCurrentEventForPendingChanges({
            changes: !isEqual(
              getState(this.store).event.current.projectEvent.event,
              getState(this.store).event.current.projectEventSnapshot.event,
            ),
          }),
        ];
      }),
      catchError(() => []),
    );
  });
  softDeleteProjectEvent = createEffect(() => {
    return this.actions.pipe(
      ofType(softDeleteProjectEvent),
      withLatestFrom(this.store.select(selectListProjectEventsState)),
      mergeMap(([{ id }, events]) => {
        const event = {
          ...events.find(event => event.id === id),
          isDeleted: true,
        };

        return this.eventService.updateProjectEvent(event).pipe(
          switchMap(() => [
            removeProjectEvent({ projectEventId: id }),
            successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENT_DELETED' }),
          ]),
          catchError(() => {
            return [
              errorPopup({
                error: 'STUDY.EVENTS.MESSAGES.UNABLE_TO_DELETE_EVENT',
              }),
            ];
          }),
        );
      }),
    );
  });
  deleteProjectEvent = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteProjectEvent),
      mergeMap(({ id }) =>
        this.eventService.deleteProjectEvent(id).pipe(
          mergeMap(() => [
            removeProjectEvent({ projectEventId: id }),
            successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENT_DELETED' }),
          ]),
          catchError(() => {
            return [
              errorPopup({
                error: 'STUDY.EVENTS.MESSAGES.UNABLE_TO_DELETE_EVENT',
              }),
            ];
          }),
        ),
      ),
    );
  });
  // project events
  updateProjectEvent = createEffect(() => {
    return this.actions.pipe(
      ofType(updateProjectEvent),
      mergeMap(({ projectEvent }) => {
        return this.eventService.updateProjectEvent(projectEvent).pipe(
          mergeMap((res: ProjectEventModel) => {
            return [
              loadProjectEventListByProjectId({ id: res.projectId }),
              updateProjectEventInList({ projectEvent: res }),
              populateCurrentProjectEvent({ projectEvent: res }),
              populateCurrentProjectEventSnapshot({ projectEvent: res }),
              successCreateUpdateEvent({ success: true }),
              pendingCreateUpdateEvent({ pending: false }),
              dispatchedCreateUpdateEvent({ dispatched: false }),
              successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENT_UPDATED' }),
            ];
          }),
          catchError(err => {
            const error = this.utilsService.extractErrorMessage(
              err.error?.error || '',
              'STUDY.EVENTS.MESSAGES.UNABLE_TO_UPDATE_EVENT',
            );

            return [
              pendingCreateUpdateEvent({ pending: false }),
              dispatchedCreateUpdateEvent({ dispatched: false }),
              errorPopup({
                error,
              }),
            ];
          }),
        );
      }),
    );
  });
  updateProjectEvents = createEffect(() => {
    return this.actions.pipe(
      ofType(updateProjectEvents),
      mergeMap(({ projectEvents }) => {
        return this.eventService.updateProjectEvents(projectEvents).pipe(
          mergeMap((res: ProjectEventModel[]) => {
            return [
              updateProjectEventsInList({ projectEvents: res }),
              successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENTS_UPDATED' }),
            ];
          }),
          catchError(() => {
            return [
              errorPopup({
                error: 'STUDY.EVENTS.MESSAGES.UNABLE_TO_UPDATE_EVENTS',
              }),
            ];
          }),
        );
      }),
    );
  });
  createProjectEvent = createEffect(() => {
    return this.actions.pipe(
      ofType(createProjectEvent),
      mergeMap(({ projectEvent }) =>
        this.eventService.createProjectEvent(projectEvent).pipe(
          mergeMap((res: ProjectEventModel) => [
            loadProjectEventListByProjectId({ id: res.projectId }),
            populateCurrentProjectEvent({ projectEvent: res }),
            populateCurrentProjectEventSnapshot({ projectEvent: res }),
            successCreateUpdateEvent({ success: true }),
            pendingCreateUpdateEvent({ pending: false }),
            dispatchedCreateUpdateEvent({ dispatched: false }),
            successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENT_CREATED' }),
          ]),
          catchError(err => {
            const error = this.utilsService.extractErrorMessage(
              err.error?.error || '',
              'STUDY.EVENTS.MESSAGES.UNABLE_TO_CREATE_EVENT',
            );
            return [
              pendingCreateUpdateEvent({ pending: false }),
              dispatchedCreateUpdateEvent({ dispatched: false }),
              errorPopup({
                error,
              }),
            ];
          }),
        ),
      ),
    );
  });
  loadProjectEventListByProjectId = createEffect(() => {
    return this.actions.pipe(
      ofType(loadProjectEventListByProjectId),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.eventService.getProjectEventListByProjectId(action.id).pipe(
          mergeMap((res: IProjectEventsResponse) => {
            return [
              updateProjectEventList({
                projectEventList: this.mergeProjectEvents(res),
              }),
              loadProjectEventsCommentsState({ projectId: action.id, addLoader: action.addLoader }),
              removeLoader(action),
              globalLoading(false),
            ];
          }),
          catchError(() => {
            return [removeLoader(action), globalLoading(false)];
          }),
        ),
      ),
    );
  });

  //event notifications
  loadEventNotificationsListByEventId = createEffect(() => {
    return this.actions.pipe(
      ofType(loadEventNotificationsListByEventId),
      switchMap(({ id }) =>
        this.eventNotificationsService.getEventNotificationsByEventId(id).pipe(
          mergeMap((eventNotifications: IEventNotificationModel[]) => [
            updateEventNotificationsList({ eventNotifications }),
            globalLoading(false),
          ]),
          catchError(() => {
            return [globalLoading(false)];
          }),
        ),
      ),
    );
  });

  getEventsCommentsCounters = createEffect(() => {
    return this.actions.pipe(
      ofType(loadProjectEventsCommentsState),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      switchMap(action =>
        this.eventService.getProjectEventsCommentsCounters(action.projectId).pipe(
          mergeMap((states: ICommentsState[]) => [
            populateProjectEventsCommentsState({ states }),
            removeLoader(action),
            globalLoading(false),
          ]),
          catchError(() => {
            return [removeLoader(action), globalLoading(false)];
          }),
        ),
      ),
    );
  });

  createEventGroup = createEffect(() => {
    return this.actions.pipe(
      ofType(createEventGroup),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      switchMap(action =>
        this.eventService.createEventGroup(action.payload).pipe(
          mergeMap((res: IEventGroup) => [
            removeLoader(action),
            globalLoading(false),
            successCreateUpdateEvent({ success: true }),
            pendingCreateUpdateEvent({ pending: false }),
            dispatchedCreateUpdateEvent({ dispatched: false }),
            successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENT_GROUP_CREATED' }),
            loadProjectEventListByProjectId({ id: res.projectId }),
          ]),
          catchError(() => {
            return [
              removeLoader(action),
              globalLoading(false),
              pendingCreateUpdateEvent({ pending: false }),
              dispatchedCreateUpdateEvent({ dispatched: false }),
              errorPopup({ error: 'STUDY.EVENTS.MESSAGES.UNABLE_TO_CREATE_EVENT_GROUP' }),
            ];
          }),
        ),
      ),
    );
  });

  updateEventGroup = createEffect(() => {
    return this.actions.pipe(
      ofType(updateEventGroup),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      switchMap(action =>
        this.eventService.updateEventGroup(action.payload).pipe(
          mergeMap((res: IEventGroup) => [
            removeLoader(action),
            globalLoading(false),
            successCreateUpdateEvent({ success: true }),
            pendingCreateUpdateEvent({ pending: false }),
            dispatchedCreateUpdateEvent({ dispatched: false }),
            successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENT_GROUP_UPDATED' }),
            loadProjectEventListByProjectId({ id: res.projectId }),
          ]),
          catchError(() => {
            return [
              removeLoader(action),
              globalLoading(false),
              pendingCreateUpdateEvent({ pending: false }),
              dispatchedCreateUpdateEvent({ dispatched: false }),
              errorPopup({ error: 'STUDY.EVENTS.MESSAGES.UNABLE_TO_UPDATE_EVENT_GROUP' }),
            ];
          }),
        ),
      ),
    );
  });

  deleteEventGroup = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteEventGroup),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      switchMap(action =>
        this.eventService.deleteEventGroup(action.data).pipe(
          mergeMap(() => [
            removeLoader(action),
            globalLoading(false),
            removeProjectEvent({ projectEventId: action.data.groupId }),
            successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENT_GROUP_DELETED' }),
          ]),
          catchError(() => {
            return [
              removeLoader(action),
              globalLoading(false),
              errorPopup({ error: 'STUDY.EVENTS.MESSAGES.UNABLE_TO_DELETE_EVENT_GROUP' }),
            ];
          }),
        ),
      ),
    );
  });

  softDeleteEventGroup = createEffect(() => {
    return this.actions.pipe(
      ofType(softDeleteEventGroup),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      switchMap(action =>
        this.eventService.softDeleteEventGroup(action.data).pipe(
          mergeMap(() => [
            removeLoader(action),
            globalLoading(false),
            removeProjectEvent({ projectEventId: action.data.groupId }),
            successPopup({
              message: 'STUDY.EVENTS.MESSAGES.EVENT_GROUP_DELETED',
            }),
          ]),
          catchError(() => {
            return [
              removeLoader(action),
              globalLoading(false),
              errorPopup({ error: 'STUDY.EVENTS.MESSAGES.UNABLE_TO_DELETE_EVENT_GROUP' }),
            ];
          }),
        ),
      ),
    );
  });

  generateEventsInGroup = createEffect(() => {
    return this.actions.pipe(
      ofType(generateEventsInGroup),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      switchMap(action =>
        this.eventService.generateEventsInGroup(action.data).pipe(
          mergeMap(() => [
            removeLoader(action),
            globalLoading(false),
            successCreateUpdateEvent({ success: true }),
            pendingCreateUpdateEvent({ pending: false }),
            dispatchedCreateUpdateEvent({ dispatched: false }),
            successPopup({ message: 'STUDY.EVENTS.MESSAGES.EVENTS_GENERATED' }),
            loadProjectEventListByProjectId({ id: action.data.projectId }),
          ]),
          catchError(err => {
            return [
              removeLoader(action),
              globalLoading(false),
              pendingCreateUpdateEvent({ pending: false }),
              dispatchedCreateUpdateEvent({ dispatched: false }),
              errorPopup({
                error: this.utilsService.extractErrorMessage(
                  err.error?.error || '',
                  'STUDY.EVENTS.MESSAGES.UNABLE_TO_GENERATE_EVENTS',
                ),
              }),
            ];
          }),
        ),
      ),
    );
  });

  private mergeProjectEvents(projectEventsResponse: IProjectEventsResponse): ProjectEventModel[] {
    const ungrouped = projectEventsResponse?.ungrouped ?? [];

    const groupsRaw: any = projectEventsResponse?.groups ?? [];
    const groupedFlattened: ProjectEventModel[] = groupsRaw.flatMap(
      (g: { groupId: number; groupName: string; events: ProjectEventModel[] }) => {
        const sortedChildren = (g.events || []).sort((a, b) => this.utilsService.compareEvents(a.event, b.event));
        const firstChild = sortedChildren[0];
        const header: ProjectEventModel = {
          id: g.groupId,
          eventId: g.groupId,
          status: Statuses.Draft,
          event: {
            id: g.groupId,
            eventName: g?.groupName ?? 'Group',
            elementType: ElementTypeEnum.Group, // here we are converting the group to a ProjectEvent looks like element
            status: Statuses.Draft,
            start: firstChild?.event?.start,
            unit: firstChild?.event?.unit,
          },
        } as unknown as ProjectEventModel;
        return [header, ...(g?.events ?? [])];
      },
    );

    const allEvents: ProjectEventModel[] = [...groupedFlattened, ...ungrouped];

    return allEvents.reduce((acc, item) => {
      if (!item || item.isDeleted) {
        return acc;
      }
      if (!item.projectEventGuid) {
        acc.push(item);
      } else {
        const existingItem = acc.find(x => x.projectEventGuid === item.projectEventGuid);
        if (existingItem) {
          if ((item.sofaVersionId ?? 0) > (existingItem.sofaVersionId ?? 0)) {
            acc[acc.indexOf(existingItem)] = item;
          }
        } else {
          acc.push(item);
        }
      }
      return acc;
    }, [] as ProjectEventModel[]);
  }
}
